import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';
import { useSnackbar } from 'notistack';

import Modal from '../../../../components/Modal/Modal';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';
import { clearPatchOrderedParts } from '../../../../actions/coilovers/orderedParts/patchOrderedParts';
import Text from '../../../../components/Text/Text';
import Input from '../../../../components/Input/Input';
import CustomSelect from '../../../../components/Select/CustomSelect';
import Button from '../../../../components/Button/Button';
import ButtonTypes from '../../../../constants/buttonTypes';
import { geUuid } from '../../../../utils/helpers';
import postOrderedParts, { clearPostOrderedParts } from '../../../../thunks/coilovers/orderedParts/postOrderedParts';
import Alert from '../../../../components/Alert/Alert';
import AlertType from '../../../../common/alertType';
import getNextIncrementValue from '../../../../thunks/general/getNextIncrementValue';
import incrementTypes from '../../../../constants/incrementTypes';
import postVendors, { postVendorsList } from '../../../../constants/postVendors';
import getBrandList from '../../../../thunks/general/getBrandList';
import getOrderedPartTypeList from '../../../../thunks/general/getOrderedPartTypeList';
import OrderedPartsNewItem from './OrderedPartsNewItem';
import { clearWarehouseMpnInfo } from '../../../../thunks/coilovers/warehouse/getWarehouseMpnInfo';
import { orderedTypesList } from '../../../../constants/orderedTypes';

const modalBodyClassName = 'w-[500px] overflow-auto px-[10px]';
const modalBodyNewClassName = 'overflow-visible';
const rowClassName = 'flex flex-row gap-[16px] mb-[16px]';
const columnClassName = 'flex-1';
const dividerClassName = 'mr-[20px]';
const labelContainerClassName = 'mb-[6px]';
const labelTextClassName = 'text-[13px]';
const inputClassName = 'py-[11px] px-[13px] font-normal text-[13px]';

const OrderedPartsNewModal = ({ visible, toggleVisible }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [toggleVendorField, setToggleVendorField] = useState(false);
  const [calculateTotalModal, setCalculateTotalModal] = useState(false);
  const dispatch = useDispatch();
  const {
    postOrderedParts: { loading, error, data: postResult },
    getNextIncrementValue: { data: { co_order_po_id: coOrderPoId, count: orderedPartId } } = {},
    brandList,
    brandListLoading,
    getOrderedPartsList,
    getOrderedPartsListLoading
  } = useSelector((state) => ({
    postOrderedParts: state?.postOrderedParts,
    getNextIncrementValue: state?.getNextIncrementValue,
    brandList: state?.getBrandList.data,
    brandListLoading: state?.getBrandList.loading,
    getOrderedPartsList: state?.getOrderedPartsList?.data,
    getOrderedPartsListLoading: state?.getOrderedPartsList?.loading,
  }), shallowEqual);

  const [formData, setFormData] = useState({
    ordered_part_id: null,
    ordered_part_po: '',
    ordered_part_note: '',
    ordered_part_price_paid: 0,
    ordered_type: orderedTypesList[0],
    items: [
      {
        id: geUuid(),
        ordered_part_type: null,
        ordered_part_brand: null,
        ordered_part_mpn: '',
        ordered_part_qty_in_order: 1,
        ordered_part_price_in_order: 0,
      }
    ]
  });

  useEffect(() => {
    setFormData({ ...formData, ordered_part_po: `C${coOrderPoId}` });
  }, [coOrderPoId]);

  useEffect(() => {
    setFormData({ ...formData, ordered_part_id: parseInt(orderedPartId) });
  }, [orderedPartId]);

  useEffect(() => {
    if (!getOrderedPartsList.length) {
      dispatch(getOrderedPartTypeList());
    }
  }, []);

  useEffect(() => {
    if (visible) {
      setFormData({
        ordered_part_id: null,
        ordered_part_po: '',
        ordered_part_note: '',
        ordered_part_price_paid: 0,
        ordered_type: orderedTypesList[0],
        items: [
          {
            id: geUuid(),
            ordered_part_type: null,
            ordered_part_brand: null,
            ordered_part_mpn: '',
            ordered_part_qty_in_order: 1,
            ordered_part_price_in_order: 0
          }
        ]
      });
      dispatch(getNextIncrementValue(incrementTypes.CO_ORDER_PO_ID));
      dispatch(getNextIncrementValue(incrementTypes.COUNT));
    }
  }, [visible]);

  useEffect(() => {
    if (postResult) {
      toggleVisible();
      enqueueSnackbar('Part create successfully', { variant: 'success' });
      batch(() => {
        dispatch(clearPostOrderedParts());
        dispatch(clearWarehouseMpnInfo());
      });
    }
  }, [postResult]);

  useEffect(() => {
    if (!brandList.length && !brandListLoading) {
      dispatch(getBrandList());
    }
  }, []);

  const onAddItem = () => setFormData({
    ...formData,
    items: [...formData.items, {
      id: geUuid(),
      ordered_part_type: null,
      ordered_part_brand: null,
      ordered_part_included: null,
      ordered_part_mpn: '',
      ordered_part_qty_in_order: 1,
      ordered_part_price_in_order: 0
    }]
  });

  const onRemoveItem = (id) => setFormData({
    ...formData,
    items: [...formData.items.filter((item) => id !== item.id)]
  });

  const onChangeItem = (id, data) => {
    setFormData({
      ...formData,
      items: [
        ...formData.items.map((item) => (item.id === id ? { ...item, ...data } : item))
      ]
    });
  };

  const onConfirm = (calcTotal) => {
    setConfirmVisible(false);
    setCalculateTotalModal(false);
    const totalItemPrice = formData?.items
      ?.map(({ costFor }) => +costFor)
      .reduce((a, b) => a + b, 0);
    const totalOrder = calcTotal ? totalItemPrice : (formData?.ordered_part_price_paid || 0);
    const totalQty = formData?.items
      ?.map(({ ordered_part_qty_in_order: qty }) => qty)
      .reduce((a, b) => a + b, 0);

    const additionalCost = (totalOrder - totalItemPrice) / totalQty;
    dispatch(postOrderedParts({
      ...formData,
      ordered_part_note: formData?.ordered_part_note?.trim(),
      ...(calcTotal && ({
        ordered_part_price_paid: totalItemPrice
      })),
      ordered_type: formData.ordered_type?.value,
      items: formData.items.map((item) => ({
        ...item,
        ordered_part_brand: item.ordered_part_brand?.value,
        ordered_part_type: item.ordered_part_type?.value,
        ordered_part_included: item.ordered_part_included?.value,
        ordered_part_price_in_order: parseFloat(+item.cost + additionalCost).toFixed(2)
      }))
    }));
  };

  return (
    <>
      <Modal
        visible={visible}
        toggleVisible={() => {
          toggleVisible();
          dispatch(clearPatchOrderedParts());
        }}
        title="New ordered part"
        subTitle={`id: ${orderedPartId}`}
        onSave={() => {
          if (formData?.ordered_part_price_paid === 0) {
            setCalculateTotalModal(true);
          } else {
            setConfirmVisible(true);
          }
        }}
        canSave={loading}
        additionalButton={(
          <Button
            text="Add Item"
            onClick={onAddItem}
            type={ButtonTypes.LIGHT_SUCCESS}
          />
        )}
      >
        <div className={`${modalBodyClassName} ${modalBodyNewClassName} customScroll`}>
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="PO Number:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                type="text"
                name="poNumber"
                value={formData?.ordered_part_po}
                placeholder="PO Number"
                disabled
                onChange={({ target: { value } }) => setFormData({ ...formData, ordered_part_po: value.trim() })}
              />
            </div>
            <div className={dividerClassName} />
            <div className={columnClassName}>
              <Text text="Price for order:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                type="number"
                step={0.01}
                name="pricePaid"
                value={`${formData?.ordered_part_price_paid}`}
                placeholder="Price paid"
                onChange={({ target: { value } }) => setFormData({
                  ...formData,
                  ordered_part_price_paid: parseFloat(value)
                })}
              />
            </div>
            <div className={dividerClassName} />
            <div className={columnClassName}>
              <Text text="Vendor:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              {toggleVendorField ? (
                <Input
                  containerStyle="d-col"
                  inputStyle={inputClassName}
                  type="text"
                  name="partVendor"
                  value={formData?.ordered_part_vendor}
                  placeholder="Vendor"
                  isClearable
                  onChange={({ target: { value } }) => setFormData({ ...formData, ordered_part_vendor: value })}
                  infoText="Go to select vendor"
                  onInfoClick={() => {
                    setToggleVendorField(false);
                    setFormData({ ...formData, ordered_part_vendor: '' });
                  }}
                />
              ) : (
                <CustomSelect
                  isSearchable
                  options={postVendorsList}
                  onChange={(data) => {
                    if (data?.value !== postVendors?.CUSTOM) {
                      setFormData({ ...formData, ordered_part_vendor: data?.value?.trim() });
                    } else {
                      setToggleVendorField(true);
                      setFormData({ ...formData, ordered_part_vendor: '' });
                    }
                  }}
                  value={[postVendorsList.find((vendor) => vendor.value === formData?.ordered_part_vendor)]}
                />
                )}
            </div>
          </div>
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="Note:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                type="text"
                name="partNote"
                value={formData?.ordered_part_note}
                placeholder="Note"
                onChange={({ target: { value } }) => setFormData({ ...formData, ordered_part_note: value })}
              />
            </div>
          </div>
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="Ordered type:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <CustomSelect
                value={[formData?.ordered_type]}
                placeholder="Select ordered type"
                options={orderedTypesList}
                onChange={(data) => setFormData({ ...formData, ordered_type: data })}
              />
            </div>
          </div>
          <br />

          { formData?.items && formData.items.map((item) => (
            <OrderedPartsNewItem
              key={item?.id}
              showDelete={formData?.items?.length > 1}
              onDelete={onRemoveItem}
              item={item}
              onChangeItem={onChangeItem}
              getOrderedPartsListLoading={getOrderedPartsListLoading}
              getOrderedPartsList={getOrderedPartsList}
              brandList={brandList}
              brandListLoading={brandListLoading}
            />
          ))}
          {error && <Alert text={error} type={AlertType.warning} />}
        </div>
      </Modal>
      <ConfirmModal
        onConfirm={() => onConfirm(false)}
        visible={confirmVisible}
        onCancel={() => {
          setConfirmVisible(false);
        }}
      />
      <ConfirmModal
        onConfirm={() => onConfirm(true)}
        visible={calculateTotalModal}
        onCancel={() => {
          setCalculateTotalModal(false);
        }}
        confirmText="Total is Empty, do you want to auto-calculate (Yes) or you will fill again?"
        cancelButtonText="No"
        confirmButtonText="Yes"
      />
    </>
  );
};

OrderedPartsNewModal.propTypes = {
  toggleVisible: PropTypes.func,
  visible: PropTypes.bool,
};

OrderedPartsNewModal.defaultProps = {
  toggleVisible: () => {},
  visible: false,
};

export default OrderedPartsNewModal;
